import {
  Building2, Layers, Smartphone, MonitorSmartphone, MessageCircle, Sparkles,
  Bot, Code2, GitBranch, Cloud, LifeBuoy, Users, Package, Target, Compass,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Services", href: "#services" },
  { label: "Solutions", href: "#solutions" },
  { label: "AI", href: "#ai" },
  { label: "How We Work", href: "#process" },
  { label: "About", href: "#why" },
  { label: "Contact", href: "#contact" },
];

export const HERO_NODES = [
  { name: "Enterprise", pos: [2.7, 0.5, 0.2] },
  { name: "SaaS", pos: [-2.5, 1.0, 0.5] },
  { name: "Web", pos: [1.7, 1.9, -1.1] },
  { name: "Mobile", pos: [-1.9, -1.5, 1.1] },
  { name: "AI", pos: [0.4, 2.5, 0.9] },
  { name: "Cloud", pos: [2.1, -1.7, -0.7] },
  { name: "DevOps", pos: [-2.9, -0.5, -1.0] },
  { name: "Integrations", pos: [0.2, -2.5, -1.3] },
];

export const SERVICES = [
  { icon: Building2, title: "Enterprise Software", desc: "Scalable, secure and maintainable software for complex business operations." },
  { icon: Layers, title: "Enterprise SaaS Software", desc: "Multi-tenant SaaS platforms built for scale, reliability and growth." },
  { icon: Smartphone, title: "Mobile App Development", desc: "Native-quality Android and iOS experiences for customers, employees and operations." },
  { icon: MonitorSmartphone, title: "Web Application Development", desc: "Modern, responsive, high-performance web applications with robust backend architecture." },
  { icon: MessageCircle, title: "WhatsApp Integration", desc: "WhatsApp in your business workflows — communication, notifications, engagement and automation." },
  { icon: Sparkles, title: "Generative AI", desc: "Practical LLM-powered experiences for content, automation, knowledge workflows and business apps." },
  { icon: Bot, title: "Agentic AI", desc: "AI agents that reason, use tools, interact with systems and automate multi-step workflows." },
  { icon: Code2, title: "Custom Software Development", desc: "Software built around your unique requirements — not your business forced into generic products." },
  { icon: GitBranch, title: "DevOps & Cloud Ops", desc: "CI/CD, infrastructure automation, observability, deployment, reliability and cloud operations." },
  { icon: Cloud, title: "AWS / Azure / GCP", desc: "Cloud-native applications designed, deployed and operated across AWS, Azure and Google Cloud." },
  { icon: LifeBuoy, title: "Application Support & Maintenance", desc: "Monitoring, issue resolution, optimization, upgrades and maintenance for production applications." },
];

export const NETWORK_NODES = [
  { id: "platform", name: "Platform", pos: [0, 0.2, 0], desc: "The core system — your product's heart: business logic, data models and domain services engineered for change." },
  { id: "ai", name: "AI", pos: [0, 2.3, 0], desc: "Generative and agentic AI capabilities embedded into the platform — reasoning, content, automation." },
  { id: "web", name: "Web", pos: [-3.1, 1.0, 0.3], desc: "High-performance, responsive web applications your customers and teams use every day." },
  { id: "mobile", name: "Mobile", pos: [3.1, 1.0, 0.3], desc: "Native-quality Android and iOS apps connected to the same platform core." },
  { id: "apis", name: "APIs", pos: [-1.7, -0.9, 0.7], desc: "Well-designed APIs that connect your product to partners, systems and future interfaces." },
  { id: "saas", name: "SaaS", pos: [1.8, -0.8, 0.8], desc: "Multi-tenant architecture with isolation, metering, billing-readiness and scale built in." },
  { id: "whatsapp", name: "WhatsApp", pos: [3.2, -0.5, -1.0], desc: "Business communication, notifications and workflow automation over WhatsApp." },
  { id: "cloud", name: "Cloud", pos: [-1.5, -2.2, 0], desc: "Cloud-native infrastructure on AWS, Azure or GCP — elastic, observable and cost-aware." },
  { id: "devops", name: "DevOps", pos: [1.6, -2.2, -0.2], desc: "CI/CD pipelines, infrastructure as code and operations that keep releases boring and reliable." },
  { id: "data", name: "Data", pos: [-3.2, -0.6, -0.9], desc: "Data storage, pipelines and analytics foundations your product and AI features depend on." },
];

export const NETWORK_EDGES = [
  ["ai", "platform"], ["web", "platform"], ["mobile", "platform"],
  ["platform", "apis"], ["platform", "saas"], ["mobile", "whatsapp"],
  ["apis", "cloud"], ["apis", "data"], ["cloud", "devops"], ["saas", "apis"],
];

export const BUILD_CATEGORIES = [
  { title: "Enterprise Platforms", art: "dashboard" },
  { title: "SaaS Products", art: "layers" },
  { title: "Customer Applications", art: "browser" },
  { title: "Mobile Applications", art: "mobile" },
  { title: "Business Automation", art: "flow" },
  { title: "AI-powered Applications", art: "ai" },
  { title: "API Platforms", art: "api" },
  { title: "Cloud-native Systems", art: "cloud" },
  { title: "Internal Business Applications", art: "console" },
  { title: "Digital Transformation Platforms", art: "transform" },
];

export const AI_STEPS = ["User", "AI Agent", "Reason", "Use Tools", "Business Systems", "Action", "Result"];

export const CLOUD_ITEMS = ["AWS", "Azure", "Google Cloud", "Docker", "CI/CD", "Monitoring", "Infrastructure", "Security"];

export const CLOUD_CAPABILITIES = [
  "Cloud architecture", "CI/CD", "Deployment automation",
  "Monitoring", "Scalability", "Reliability",
];

export const PROCESS_STEPS = [
  { num: "01", title: "Discover", desc: "Understand the business, users and requirements." },
  { num: "02", title: "Architect", desc: "Define technology, architecture and engineering approach." },
  { num: "03", title: "Build", desc: "Develop the product using modern engineering practices." },
  { num: "04", title: "Deploy", desc: "Set up cloud infrastructure, CI/CD and production environments." },
  { num: "05", title: "Scale", desc: "Improve performance, reliability, security and capabilities." },
  { num: "06", title: "Support", desc: "Provide ongoing application support and maintenance." },
];

export const PRINCIPLES = [
  { title: "Senior Engineering Talent", desc: "Experienced engineers with strong production software development backgrounds." },
  { title: "Enterprise-Grade Engineering", desc: "Architecture and implementation designed for reliability, scalability and maintainability." },
  { title: "End-to-End Ownership", desc: "From architecture and development to deployment and ongoing support." },
  { title: "Modern Technology", desc: "Cloud, AI, SaaS, mobile, web and modern software engineering practices." },
  { title: "Business Understanding", desc: "Technology decisions aligned with actual business objectives." },
  { title: "Long-Term Partnership", desc: "Support beyond the initial development lifecycle." },
];

export const ENGAGEMENT_MODELS = [
  { icon: Users, title: "Dedicated Engineering Team", desc: "Extend your engineering capacity with experienced developers." },
  { icon: Package, title: "Product Development", desc: "Build your complete product from idea to production." },
  { icon: Target, title: "Project-Based Development", desc: "Bring a clearly defined software project and let our engineering team deliver it." },
  { icon: Compass, title: "Technology Partnership", desc: "Senior technical expertise for architecture, modernization and engineering decisions." },
];

export const INTEREST_OPTIONS = [
  "Enterprise Software", "Enterprise SaaS", "Mobile App Development", "Web Application Development",
  "WhatsApp Integration", "Generative AI", "Agentic AI", "Custom Software Development",
  "DevOps & Cloud Ops", "AWS / Azure / GCP", "Application Support & Maintenance", "Something else",
];

export const EXPERIENCE_ORGS = ["IBM", "Oracle", "Amazon", "Microsoft"];
